import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AuthCard } from '@/components/AuthCard';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { resetPassword } from '@/lib/auth-client';
import { resetPasswordSchema } from '@shared/schemas/user';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  // No token means the link was mistyped, truncated by an email client, or
  // already used. Say which, and give a way forward — an empty form that fails
  // on submit teaches nothing.
  if (!token) {
    return (
      <AuthCard
        title="That link has expired"
        description="Reset links last an hour and work once. Ask for a new one."
        footer={
          <Link className="text-body text-bark-700 underline underline-offset-4" to="/login">
            Back to sign in
          </Link>
        }
      >
        <Button asChild full size="lg">
          <Link to="/forgot-password">Send a new link</Link>
        </Button>
      </AuthCard>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setFieldErrors({});

    // The same schema the server enforces, so the rules and the wording match
    // on both sides.
    const parsed = resetPasswordSchema.safeParse({ token, password, confirmPassword });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message]),
        ),
      );
      return;
    }

    setSubmitting(true);
    const result = await resetPassword({ token, newPassword: password });
    setSubmitting(false);

    if (result.error) {
      setError('That link has expired or has already been used. Ask for a new one.');
      return;
    }

    // Straight to sign-in, not to the app: resetting revokes every other
    // session and does not create one here, so they still have to sign in.
    await navigate('/login?reset=1', { replace: true });
  }

  return (
    <AuthCard title="Set a new password" description="At least 12 characters.">
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5" noValidate>
        <FormError>{error}</FormError>

        <Field
          label="New password"
          name="new-password"
          type="password"
          autoComplete="new-password"
          autoFocus
          required
          error={fieldErrors.password}
          hint="Twelve characters or more. A short phrase is easier to remember than a jumble."
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />

        <Field
          label="Confirm new password"
          name="confirm-password"
          type="password"
          autoComplete="new-password"
          required
          error={fieldErrors.confirmPassword}
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
          }}
        />

        <Button type="submit" full size="lg" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save and sign in'}
        </Button>
      </form>
    </AuthCard>
  );
}
